/**
 * Global 404/Error Page Handler
 * Catches any page not found errors and displays a user-friendly page
 */

export function initializeGlobal404Handler(): void {
  // Intercept document.write to catch error pages being rendered
  const originalWrite = document.write.bind(document);
  document.write = function(...args: any[]): void {
    const content = args.join('');
    
    // If trying to write error HTML (XML error page), show our error page instead
    if (
      content.includes('<?xml') ||
      content.includes('NoSuchKey') ||
      content.includes('<Error>') ||
      content.includes('Access Denied') ||
      content.includes('The specified key does not exist')
    ) {
      console.warn('Error page detected, showing friendly error');
      showFriendlyErrorPage();
      return;
    }
    
    originalWrite(content);
  };

  // Listen for unhandled errors
  window.addEventListener('error', (event) => {
    console.error('Global error caught:', event.error);
  });

  // Listen for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
  });

  // Monitor DOMContentLoaded to check for error pages
  document.addEventListener('DOMContentLoaded', () => {
    checkAndReplaceErrorPage();
  });

  // Also check on load
  window.addEventListener('load', () => {
    checkAndReplaceErrorPage();
  });

  // Check periodically for error pages
  setInterval(checkAndReplaceErrorPage, 500);
}

function checkAndReplaceErrorPage(): void {
  const html = document.documentElement.innerText || document.documentElement.textContent || '';
  
  if (
    html.includes('<?xml') ||
    html.includes('NoSuchKey') ||
    html.includes('<Error>') ||
    html.includes('Access Denied') ||
    html.includes('The specified key does not exist') ||
    html.includes('XML file does not appear to have any style')
  ) {
    console.warn('Error page detected on page, replacing with friendly error');
    showFriendlyErrorPage();
  }
}

function showFriendlyErrorPage(): void {
  const errorHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Page Not Found</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .error-container {
          text-align: center;
          max-width: 600px;
          animation: slideUp 0.5s ease-out;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .error-icon {
          font-size: 80px;
          margin-bottom: 20px;
          animation: bounce 2s infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        h1 {
          font-size: 48px;
          font-weight: 700;
          margin-bottom: 10px;
          color: #1a1a1a;
          letter-spacing: -0.5px;
        }
        .error-message {
          font-size: 18px;
          color: #666;
          margin-bottom: 30px;
          line-height: 1.5;
        }
        .error-help {
          font-size: 14px;
          color: #888;
          margin-bottom: 30px;
          line-height: 1.6;
        }
        .error-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .error-button {
          padding: 12px 32px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-decoration: none;
          display: inline-block;
        }
        .error-button--primary {
          background: #1a1a1a;
          color: #ffffff;
        }
        .error-button--primary:hover {
          background: #333;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .error-button--secondary {
          background: transparent;
          color: #1a1a1a;
          border: 1px solid #1a1a1a;
        }
        .error-button--secondary:hover {
          background: #f0f0f0;
          transform: translateY(-2px);
        }
        @media (max-width: 640px) {
          h1 { font-size: 36px; }
          .error-message { font-size: 16px; }
          .error-icon { font-size: 60px; }
          .error-actions { flex-direction: column; }
          .error-button { width: 100%; }
        }
      </style>
    </head>
    <body>
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h1>Oops!</h1>
        <p class="error-message">This page doesn't exist.</p>
        <p class="error-help">
          If you came from a link, it might be broken. Please go back to browse our collection.
        </p>
        <div class="error-actions">
          <a href="https://micahlynise.com" class="error-button error-button--primary">
            Go Home
          </a>
          <button class="error-button error-button--secondary" onclick="window.history.back()">
            Go Back
          </button>
        </div>
      </div>
    </body>
    </html>
  `;

  document.open();
  document.write(errorHTML);
  document.close();
}
